import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import XLSX from 'xlsx-js-style';
import { adminAPI } from '../../../services/api';

// Sub-asset classes treated as overlays — excluded from the physical 100% total.
const OVERLAY_SACS = new Set(['CURRENCY']);

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// ─── Excel template styling ───────────────────────────────────────────────────
// Mirrors the Portfolio Optimiser "Blended Profiles" export (navy header, orange
// totals) so the download lays out identically and pastes back cleanly.
const SAA_XLSX = {
  header: {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
    fill: { fgColor: { rgb: '072040' } },
    alignment: { horizontal: 'center' },
  },
  total: {
    font: { bold: true, color: { rgb: '072040' }, sz: 10 },
    fill: { fgColor: { rgb: 'F0A500' } },
  },
  cell: { font: { sz: 10 }, border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } } },
};
SAA_XLSX.totalNum = { ...SAA_XLSX.total, alignment: { horizontal: 'right' }, numFmt: '0.0' };
SAA_XLSX.num = { ...SAA_XLSX.cell, alignment: { horizontal: 'right' }, numFmt: '0.0' };

const HistoricSAAEditor = () => {
  const [portfolios, setPortfolios] = useState([]);   // [{blender_code, name}]
  const [allSacs, setAllSacs] = useState([]);         // taxonomy order
  const [rows, setRows] = useState([]);               // [{blender_code, financial_year, sub_asset_class, weight(pct)}]
  const [years, setYears] = useState([]);             // sorted desc
  const [selectedFY, setSelectedFY] = useState(null);
  const [activeFY, setActiveFY] = useState(null);     // the live/default SAA year

  // Editable grid for the selected FY: { blender_code: { sac: stringPct } }
  const [grid, setGrid] = useState({});
  const [gridPorts, setGridPorts] = useState([]);     // blender_codes shown as rows
  const [gridSacs, setGridSacs] = useState([]);       // sub-asset-classes shown as columns

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copySource, setCopySource] = useState('');   // blender_code to copy weights FROM
  const [copyTarget, setCopyTarget] = useState('');   // blender_code to copy weights INTO
  const fileInputRef = useRef(null);

  const portName = useMemo(() => {
    const m = {};
    portfolios.forEach((p) => { m[p.blender_code] = p.name; });
    return m;
  }, [portfolios]);

  // blender_codes with historic SAA weights but no portfolio-registry entry yet
  const unregistered = useMemo(
    () => new Set(portfolios.filter((p) => p.unregistered).map((p) => p.blender_code)),
    [portfolios]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getHistoricSAA();
      setPortfolios(data.portfolios || []);
      setAllSacs(data.sub_asset_classes || []);
      setRows(data.rows || []);
      const fys = data.financial_years || [];
      setYears(fys);
      setActiveFY(data.active_financial_year ?? null);
      setSelectedFY((prev) => (prev && fys.includes(prev)) ? prev : (fys[0] ?? null));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load historic SAA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build the editable grid whenever the selected FY (or source rows) change.
  useEffect(() => {
    if (selectedFY == null) { setGrid({}); setGridPorts([]); setGridSacs([]); return; }
    const fyRows = rows.filter((r) => r.financial_year === selectedFY);
    const g = {};
    const portsInFy = [];
    const sacsInFy = new Set();
    fyRows.forEach((r) => {
      if (!g[r.blender_code]) { g[r.blender_code] = {}; portsInFy.push(r.blender_code); }
      g[r.blender_code][r.sub_asset_class] = String(r.weight);
      sacsInFy.add(r.sub_asset_class);
    });
    // Order rows by the portfolios list; columns by taxonomy order.
    const orderedPorts = portfolios.map((p) => p.blender_code).filter((bc) => portsInFy.includes(bc));
    const orderedSacs = allSacs.filter((s) => sacsInFy.has(s));
    setGrid(g);
    setGridPorts(orderedPorts.length ? orderedPorts : portsInFy);
    setGridSacs(orderedSacs.length ? orderedSacs : [...sacsInFy]);
  }, [selectedFY, rows, portfolios, allSacs]);

  const setCell = (bc, sac, val) => {
    setGrid((prev) => ({ ...prev, [bc]: { ...(prev[bc] || {}), [sac]: val } }));
  };

  const physicalTotal = (bc) => gridSacs.reduce(
    (sum, s) => sum + (OVERLAY_SACS.has(s) ? 0 : num(grid[bc]?.[s])), 0);

  // A portfolio must total 100% (excl. currency) to save. TOTAL_TOL only absorbs
  // sub-0.1 rounding noise (99.9% / 100.1% are rejected). A fully-empty column
  // (total ~0) is treated as not-yet-populated and allowed.
  const TOTAL_TOL = 0.05;
  const EMPTY_EPS = 0.05;
  const isTotalOff = (t) => t >= EMPTY_EPS && Math.abs(t - 100) > TOTAL_TOL;
  const invalidBcs = gridPorts.filter((bc) => isTotalOff(physicalTotal(bc)));

  const handleSave = async () => {
    if (invalidBcs.length) {
      setError(`Each portfolio must total 100% (excl. currency) to save. Off: ${invalidBcs.join(', ')}`);
      return;
    }
    setSaving(true); setError(null); setSuccess(null);
    try {
      const weights = {};
      gridPorts.forEach((bc) => {
        weights[bc] = {};
        gridSacs.forEach((s) => { weights[bc][s] = num(grid[bc]?.[s]); });
      });
      await adminAPI.saveHistoricSAA({ financial_year: selectedFY, weights });
      await fetchAll();
      setSuccess(`Saved FY${selectedFY}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Promote the selected FY to the portal-wide LIVE SAA. This is the deliberate
  // "publish" step — it propagates to the dashboard, blender, optimiser, etc.
  const handleSetActiveFY = async () => {
    if (selectedFY == null) return;
    if (selectedFY === activeFY) return;
    if (!window.confirm(
      `Make FY${selectedFY} the live SAA for the whole portal now? ` +
      `Clients and all apps (dashboard, blender, optimiser, etc.) will immediately use FY${selectedFY}.`
    )) return;
    setError(null); setSuccess(null);
    try {
      await adminAPI.setActiveSAAYear(selectedFY);
      setActiveFY(selectedFY);
      setSuccess(`FY${selectedFY} is now the live SAA across the portal.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set the active SAA year');
    }
  };

  const handleAddFY = () => {
    const input = window.prompt('New financial year (the year it ENDS, e.g. 2025):',
      selectedFY ? String(selectedFY - 1) : '2026');
    if (input == null) return;
    const fy = parseInt(input, 10);
    if (!Number.isInteger(fy) || fy < 2000 || fy > 2100) { setError('Enter a valid year (2000–2100)'); return; }
    if (years.includes(fy)) { setSelectedFY(fy); return; }
    // Seed the new FY from the currently-shown grid (copy values to edit deltas).
    const copied = rows
      .filter((r) => r.financial_year === selectedFY)
      .map((r) => ({ ...r, financial_year: fy }));
    setRows((prev) => [...prev, ...copied]);
    setYears((prev) => [...new Set([...prev, fy])].sort((a, b) => b - a));
    setSelectedFY(fy);
    setSuccess(`New FY${fy} — edit weights and Save to persist`);
  };

  const handleDeleteFY = async () => {
    if (selectedFY == null) return;
    if (!window.confirm(`Delete the entire FY${selectedFY} SAA? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteHistoricSAAYear(selectedFY);
      await fetchAll();
      setSuccess(`Deleted FY${selectedFY}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddPortfolio = (bc) => {
    if (!bc || gridPorts.includes(bc)) return;
    setGrid((prev) => ({ ...prev, [bc]: {} }));
    setGridPorts((prev) => {
      const next = [...prev, bc];
      return portfolios.map((p) => p.blender_code).filter((c) => next.includes(c));
    });
  };

  const handleAddSac = (sac) => {
    if (!sac || gridSacs.includes(sac)) return;
    setGridSacs((prev) => {
      const next = new Set([...prev, sac]);
      return allSacs.filter((s) => next.has(s));
    });
  };

  // Copy the source SAA's weights (for the selected FY) into the target row.
  // Adds the target as a grid row if it isn't shown yet, and pulls in any of the
  // source's sub-asset-class columns that aren't currently visible. Persist via Save.
  const handleCopyWeights = () => {
    setError(null); setSuccess(null);
    if (!copySource || !copyTarget) { setError('Choose both a source and a target SAA to copy between.'); return; }
    if (copySource === copyTarget) { setError('Source and target must be different SAAs.'); return; }
    const srcWeights = grid[copySource] || {};
    if (Object.keys(srcWeights).length === 0) {
      setError(`${copySource} has no weights in FY${selectedFY} to copy.`);
      return;
    }
    setGrid((prev) => ({ ...prev, [copyTarget]: { ...srcWeights } }));
    setGridPorts((prev) => {
      if (prev.includes(copyTarget)) return prev;
      const next = [...prev, copyTarget];
      // Keep the portfolios-list order; append any codes not in that list.
      const ordered = portfolios.map((p) => p.blender_code).filter((c) => next.includes(c));
      next.forEach((c) => { if (!ordered.includes(c)) ordered.push(c); });
      return ordered;
    });
    setGridSacs((prev) => {
      const union = new Set([...prev, ...Object.keys(srcWeights)]);
      const ordered = allSacs.filter((s) => union.has(s));
      [...union].forEach((s) => { if (!ordered.includes(s)) ordered.push(s); });
      return ordered;
    });
    setSuccess(`Copied weights from ${copySource} into ${copyTarget} — review and Save FY${selectedFY}.`);
    setCopyTarget('');
  };

  // ── Download an Excel template for the selected FY ──────────────────────────
  // Layout mirrors the optimiser "Blended Profiles" sheet: SACs down the rows,
  // portfolios across the columns, navy header + orange totals row. When the FY
  // is empty it falls back to the full taxonomy so a fresh SAA can be built and
  // pasted straight back via Upload.
  const handleDownloadTemplate = () => {
    setError(null); setSuccess(null);
    // Prefer the on-screen grid for portfolios; otherwise seed from the taxonomy.
    const ports = (gridPorts.length ? gridPorts : portfolios.map((p) => p.blender_code));
    // Always include the FULL SAC taxonomy (blank where unused) so the layout
    // matches the optimiser blend export and pastes row-for-row. Blank rows are
    // ignored on upload, so they never get pushed into the saved SAA.
    const extraSacs = gridSacs.filter((s) => !allSacs.includes(s));
    const sacs = [...allSacs, ...extraSacs];
    if (ports.length === 0 || sacs.length === 0) {
      setError('Nothing to download — add at least one portfolio and sub-asset class first.');
      return;
    }

    const headers = ['Sub Asset Class', ...ports];
    const aoa = [headers];
    const rowMeta = [];
    sacs.forEach((sac) => {
      const row = [OVERLAY_SACS.has(sac) ? `${sac} *` : sac];
      ports.forEach((bc) => {
        const raw = grid[bc]?.[sac];
        const v = parseFloat(raw);
        row.push(raw === '' || raw == null || !Number.isFinite(v) ? '' : v);
      });
      aoa.push(row); rowMeta.push('data');
    });
    // Total (excl. overlay) row, matching the on-screen physical total.
    const totalRow = ['Total (excl. overlay)'];
    ports.forEach((bc) => {
      const t = sacs.reduce((s, sac) => s + (OVERLAY_SACS.has(sac) ? 0 : num(grid[bc]?.[sac])), 0);
      totalRow.push(parseFloat(t.toFixed(1)));
    });
    aoa.push(totalRow); rowMeta.push('total');

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 28 }, ...ports.map(() => ({ wch: 13 }))];
    for (let c = 0; c < headers.length; c++) {
      const ref = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[ref]) ws[ref].s = SAA_XLSX.header;
    }
    for (let r = 0; r < rowMeta.length; r++) {
      const excelRow = r + 1;
      for (let c = 0; c < headers.length; c++) {
        const ref = XLSX.utils.encode_cell({ r: excelRow, c });
        if (!ws[ref]) ws[ref] = { v: '', t: 's' };
        if (rowMeta[r] === 'total') ws[ref].s = c >= 1 ? SAA_XLSX.totalNum : SAA_XLSX.total;
        else ws[ref].s = c >= 1 ? SAA_XLSX.num : SAA_XLSX.cell;
      }
    }

    const wb = XLSX.utils.book_new();
    const fyLabel = selectedFY != null ? `FY${selectedFY}` : 'Template';
    XLSX.utils.book_append_sheet(wb, ws, `${fyLabel} SAA`.slice(0, 31));
    XLSX.writeFile(wb, `Historic_SAA_${fyLabel}.xlsx`);
  };

  // ── Upload an Excel template back into the editable grid ────────────────────
  // Reads the same SAC-rows × portfolio-columns layout, merges values into the
  // grid for the selected FY, and leaves persistence to the existing Save flow.
  const handleUploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // allow re-selecting the same file later
    setError(null); setSuccess(null);
    if (selectedFY == null) {
      setError('Add or select a financial year before uploading.');
      return;
    }
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error('No sheet found in the uploaded file.');
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });

      // Locate the header row (first cell ~ "Sub Asset Class"); default to row 0.
      let headerIdx = aoa.findIndex(
        (r) => typeof r[0] === 'string' && /sub\s*asset\s*class/i.test(r[0]));
      if (headerIdx < 0) headerIdx = 0;
      const headerRow = aoa[headerIdx] || [];
      const ports = headerRow.slice(1)
        .map((h) => (h == null ? '' : String(h).trim()))
        .filter((h) => h !== '');
      if (ports.length === 0) throw new Error('No portfolio columns found in the header row.');

      const cleanSac = (s) => String(s).replace(/\s*\*\s*$/, '').trim();   // strip overlay marker
      const nextGrid = { ...grid };
      const sacsWithValues = [];   // only rows that actually carry a weight
      for (let r = headerIdx + 1; r < aoa.length; r++) {
        const row = aoa[r] || [];
        const first = row[0];
        if (first == null || String(first).trim() === '') continue;
        const sac = cleanSac(first);
        if (/^total/i.test(sac)) continue;   // skip the totals row
        let any = false;
        ports.forEach((bc, i) => {
          const v = row[i + 1];
          if (v === '' || v == null) return;
          const n = parseFloat(v);
          if (!Number.isFinite(n) || n === 0) return;   // ignore blanks and zeros
          nextGrid[bc] = { ...(nextGrid[bc] || {}), [sac]: String(n) };
          any = true;
        });
        // Rows that are entirely blank/zero are ignored — they don't enter the SAA.
        if (any && !sacsWithValues.includes(sac)) sacsWithValues.push(sac);
      }
      if (sacsWithValues.length === 0) throw new Error('No populated sub-asset-class rows found in the file.');

      // Merge portfolios/SACs into the grid axes, preserving taxonomy order.
      const portUnion = new Set([...gridPorts, ...ports]);
      const orderedPorts = portfolios.map((p) => p.blender_code).filter((bc) => portUnion.has(bc));
      [...portUnion].forEach((bc) => { if (!orderedPorts.includes(bc)) orderedPorts.push(bc); });

      const sacUnion = new Set([...gridSacs, ...sacsWithValues]);
      const orderedSacs = allSacs.filter((s) => sacUnion.has(s));
      [...sacUnion].forEach((s) => { if (!orderedSacs.includes(s)) orderedSacs.push(s); });

      setGrid(nextGrid);
      setGridPorts(orderedPorts);
      setGridSacs(orderedSacs);
      setSuccess(`Loaded ${sacsWithValues.length} populated sub-asset classes from “${file.name}” — review and Save FY${selectedFY}.`);
    } catch (err) {
      setError(`Upload failed: ${err.message || err}`);
    }
  };

  if (loading) return <div className="p-4 text-muted">Loading historic SAA…</div>;

  const portfoliosToAdd = portfolios.filter((p) => !gridPorts.includes(p.blender_code));
  const sacsToAdd = allSacs.filter((s) => !gridSacs.includes(s));

  return (
    <div className="historic-saa-editor">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <strong style={{ color: 'var(--primary-color)' }}>Historic SAA</strong>
        <span className="text-muted small">Strategic neutral weights by financial year (FY = year it ends). Only the <strong>live</strong> year is used across the portal — build other years freely; they stay hidden until you set them live.</span>
      </div>

      {error && <div className="alert alert-danger alert-dismissible">{error}<button className="btn-close" onClick={() => setError(null)} /></div>}
      {success && <div className="alert alert-success alert-dismissible">{success}<button className="btn-close" onClick={() => setSuccess(null)} /></div>}

      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <label className="small mb-0">Financial Year</label>
        <select className="form-select form-select-sm" style={{ width: 160 }}
          value={selectedFY ?? ''} onChange={(e) => setSelectedFY(parseInt(e.target.value, 10))}>
          {years.length === 0 && <option value="">— none —</option>}
          {years.map((fy) => (
            <option key={fy} value={fy}>FY{fy}{fy === activeFY ? ' ● live' : ''}</option>
          ))}
        </select>
        {selectedFY != null && (
          selectedFY === activeFY ? (
            <span className="badge" style={{ background: 'var(--success-color)', color: 'var(--primary-color)' }}
              title="This vintage is live across the whole portal">● Live</span>
          ) : (
            <button className="btn btn-sm" style={{ background: 'var(--secondary-color)', color: 'var(--primary-color)' }}
              onClick={handleSetActiveFY}
              title="Make this the live SAA for the whole portal (dashboard, blender, optimiser, etc.)">
              <i className="bi bi-broadcast me-1" />Set FY{selectedFY} as live
            </button>
          )
        )}
        <button className="btn btn-outline-primary btn-sm" onClick={handleAddFY}>
          <i className="bi bi-plus-lg me-1" />Add / clone FY
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleDownloadTemplate}
          title="Download an Excel template (laid out like the optimiser blend export) to edit weights offline">
          <i className="bi bi-download me-1" />Download template
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => fileInputRef.current?.click()}
          title="Upload a filled-in template back into the grid (review, then Save)">
          <i className="bi bi-upload me-1" />Upload
        </button>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls"
          style={{ display: 'none' }} onChange={handleUploadTemplate} />
        {selectedFY != null && (
          <button className="btn btn-outline-danger btn-sm ms-auto" onClick={handleDeleteFY}>
            <i className="bi bi-trash me-1" />Delete FY{selectedFY}
          </button>
        )}
      </div>

      {selectedFY == null ? (
        <div className="text-muted">No historic SAA yet — use “Add / clone FY” to create one.</div>
      ) : (
        <>
          <div className="table-responsive" style={{ maxHeight: '60vh' }}>
            <table className="table table-sm table-bordered align-middle" style={{ fontSize: '0.8rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--primary-color)', color: '#fff', zIndex: 1 }}>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: 'var(--primary-color)', minWidth: 150 }}>Portfolio</th>
                  {gridSacs.map((s) => (
                    <th key={s} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {s}{OVERLAY_SACS.has(s) ? ' *' : ''}
                    </th>
                  ))}
                  <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {gridPorts.map((bc) => {
                  const total = physicalTotal(bc);
                  const off = isTotalOff(total);
                  const empty = total < EMPTY_EPS;
                  const totalColor = off ? '#dc3545' : (empty ? 'var(--gray-color, #6c757d)' : 'var(--success-color, #2d8a4e)');
                  return (
                    <tr key={bc}>
                      <td style={{ position: 'sticky', left: 0, background: '#fff', fontWeight: 600 }}
                        title={unregistered.has(bc) ? `${bc} — not in the portfolio registry (weights stored, not shown elsewhere)` : (portName[bc] || '')}>
                        {bc}
                        {unregistered.has(bc) && (
                          <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 600, color: '#a06f00',
                            background: 'rgba(240,165,0,0.18)', borderRadius: 8, padding: '1px 6px', textTransform: 'uppercase' }}>
                            unregistered
                          </span>
                        )}
                      </td>
                      {gridSacs.map((s) => (
                        <td key={s} style={{ padding: 1 }}>
                          <input
                            type="number" step="0.1"
                            value={grid[bc]?.[s] ?? ''}
                            onChange={(e) => setCell(bc, s, e.target.value)}
                            style={{ width: 58, textAlign: 'right', border: '1px solid #ddd', borderRadius: 3, fontSize: '0.78rem', padding: '1px 3px' }}
                          />
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', fontWeight: 600, color: totalColor }}
                        title={off ? 'Must total 100% (excludes overlays like CURRENCY) to save'
                                   : 'Physical weight total (excludes overlays like CURRENCY)'}>
                        {total.toFixed(1)}%{off ? ' ⚠' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-muted small mb-3">* overlay (e.g. CURRENCY) — excluded from the physical 100% total.</div>

          <div className="d-flex align-items-center flex-wrap gap-2">
            {portfoliosToAdd.length > 0 && (
              <select className="form-select form-select-sm" style={{ width: 220 }} value=""
                onChange={(e) => handleAddPortfolio(e.target.value)}>
                <option value="">+ Add portfolio…</option>
                {portfoliosToAdd.map((p) => <option key={p.blender_code} value={p.blender_code}>{p.blender_code} — {p.name}</option>)}
              </select>
            )}
            {sacsToAdd.length > 0 && (
              <select className="form-select form-select-sm" style={{ width: 220 }} value=""
                onChange={(e) => handleAddSac(e.target.value)}>
                <option value="">+ Add sub-asset class…</option>
                {sacsToAdd.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {gridPorts.length > 0 && (
              <div className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                title="Copy an existing SAA's weights into another portfolio (review, then Save)">
                <span className="small text-muted">Copy from</span>
                <select className="form-select form-select-sm" style={{ width: 170 }}
                  value={copySource} onChange={(e) => setCopySource(e.target.value)}>
                  <option value="">— source SAA —</option>
                  {gridPorts.map((bc) => (
                    <option key={bc} value={bc}>{bc}{portName[bc] ? ` — ${portName[bc]}` : ''}</option>
                  ))}
                </select>
                <i className="bi bi-arrow-right text-muted" />
                <select className="form-select form-select-sm" style={{ width: 170 }}
                  value={copyTarget} onChange={(e) => setCopyTarget(e.target.value)}>
                  <option value="">— target SAA —</option>
                  {portfolios
                    .filter((p) => p.blender_code !== copySource)
                    .map((p) => (
                      <option key={p.blender_code} value={p.blender_code}>
                        {p.blender_code}{p.name ? ` — ${p.name}` : ''}{gridPorts.includes(p.blender_code) ? ' (overwrite)' : ''}
                      </option>
                    ))}
                </select>
                <button className="btn btn-outline-primary btn-sm" onClick={handleCopyWeights}
                  disabled={!copySource || !copyTarget}>
                  <i className="bi bi-files me-1" />Copy
                </button>
              </div>
            )}
            {invalidBcs.length > 0 && (
              <span className="text-danger small ms-auto me-2" style={{ fontWeight: 600 }}>
                {invalidBcs.length} portfolio{invalidBcs.length === 1 ? '' : 's'} not at 100% (excl. currency)
              </span>
            )}
            <button className={`btn btn-primary btn-sm${invalidBcs.length ? '' : ' ms-auto'}`}
              onClick={handleSave} disabled={saving || invalidBcs.length > 0}
              title={invalidBcs.length ? `Fix totals before saving: ${invalidBcs.join(', ')}` : ''}>
              {saving ? 'Saving…' : `Save FY${selectedFY}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default HistoricSAAEditor;
